import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Loader2, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * Photo-capture → vision extraction card.
 *
 * props:
 *   kind: "insurance_card" | "med_list"
 *   onExtract(payload): callback receiving the structured JSON from the API
 *   title, blurb
 *   testid
 */
export default function PhotoExtractButton({ kind, title, blurb, onExtract, testid }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const pick = () => fileRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Photo is too large (8 MB max). Retake it closer.");
      return;
    }
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type || "")) {
      toast.error("Supported formats: JPEG, PNG, WEBP.");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await axios.post(
        `${API}/ai/vision-extract`,
        { kind, image_base64: dataUrl },
        { timeout: 90000 }
      );
      setLastResult({ ok: true, ...res.data });
      onExtract?.(res.data);
      const n = kind === "med_list" ? (res.data.rows?.length || 0) : 1;
      toast.success(
        kind === "med_list"
          ? `Extracted ${n} medication${n === 1 ? "" : "s"} ✨`
          : "Insurance card read ✨ verify before saving."
      );
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setLastResult({ ok: false, error: msg });
      toast.error(`Extract failed: ${msg}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div
      className="rounded-xl border border-[#B7CADA] bg-gradient-to-br from-[#eef3ee] to-[#e6e3d8] p-4 flex flex-col gap-3"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-[#0A3D79] text-[#F7FAFC] flex items-center justify-center">
          <Camera className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[14.5px] font-semibold text-[#0B1E3A]">{title}</div>
          <div className="text-[12.5px] text-[#3A5275] leading-snug">{blurb}</div>
        </div>
        <Sparkles className="w-4 h-4 text-[#0A3D79] shrink-0" />
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        className="btn-primary !justify-center"
        data-testid={`${testid}-pick`}
      >
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading photo…</> : <><Camera className="w-4 h-4" /> Take photo</>}
      </button>

      {lastResult?.ok && (
        <div className="rounded-md bg-white border border-[#B7CADA] p-2.5 text-[12.5px] text-[#0B1E3A] flex items-start gap-2" data-testid={`${testid}-result`}>
          <CheckCircle2 className="w-4 h-4 text-[#0A3D79] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold">
              {kind === "med_list"
                ? `${lastResult.rows?.length || 0} medication${(lastResult.rows?.length || 0) === 1 ? "" : "s"} added — verify each row.`
                : "Fields populated below — verify each value."}
            </div>
            {!!(lastResult.warnings || []).length && (
              <ul className="mt-1 text-[11.5px] text-[#a07a27] list-disc pl-4">
                {lastResult.warnings.slice(0, 3).map((w) => (<li key={w}>{w}</li>))}
              </ul>
            )}
            {typeof lastResult.confidence === "number" && (
              <div className="text-[11px] text-[#3A5275] mt-0.5">
                Model confidence: {(lastResult.confidence * 100).toFixed(0)}%
              </div>
            )}
          </div>
        </div>
      )}

      {lastResult?.ok === false && (
        <div className="rounded-md bg-[#fbeae5] border border-[#e2b6a9] p-2.5 text-[12.5px] text-[#8a2e1a] flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{lastResult.error || "Extraction failed. Please retake the photo."}</div>
        </div>
      )}
    </div>
  );
}
